/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/formDefaults.ts
 🗓️ Created: 2025-01-17
 🧩 Description:
    Default form values and factory functions for Outbound Routes.
────────────────────────────────────────────────────────────────*/

import {
  OutboundRouteFormState,
  OutboundRoutePattern,
  OutboundRouteGateway
} from '../types';

/**
 * Default form data
 */
export const DEFAULT_FORM_DATA: OutboundRouteFormState = {
  name: '',
  description: '',
  priority: 100,
  routingStrategy: 'failover',
  enabled: true,
  failoverModule: 'terminate_call',
  failoverDestination: 'NORMAL_CLEARING',
  pinListId: null,
  dialProfileId: null
};

/**
 * Create new pattern
 */
export function newPattern(priority: number): OutboundRoutePattern {
  return {
    pattern: '',
    prepend: '',
    stripDigits: 0,
    description: '',
    priority,
    enabled: true
  };
}

/**
 * Create new gateway
 */
export function newGateway(priority: number): OutboundRouteGateway {
  return {
    gatewayId: 0,
    gatewayName: '',
    priority,
    weight: 0,
    buyRate: 0,
    sellRate: 0,
    enabled: true,
    description: ''
  };
}

/**
 * Clone patterns array
 */
export function clonePatterns(items: OutboundRoutePattern[]): OutboundRoutePattern[] {
  return items.map((item) => ({ ...item }));
}

/**
 * Clone gateways array
 */
export function cloneGateways(items: OutboundRouteGateway[]): OutboundRouteGateway[] {
  return items.map((item) => ({ ...item }));
}

/**
 * Resequence patterns by priority
 */
export function resequencePatterns(items: OutboundRoutePattern[]): OutboundRoutePattern[] {
  return items.map((item, index) => ({ ...item, priority: index + 1 }));
}

/**
 * Resequence gateways by priority
 */
export function resequenceGateways(items: OutboundRouteGateway[]): OutboundRouteGateway[] {
  return items.map((item, index) => ({ ...item, priority: index + 1 }));
}

/**
 * Check if form has changes
 */
export function hasFormChanges(
  formData: OutboundRouteFormState,
  originalData: OutboundRouteFormState,
  patterns: OutboundRoutePattern[],
  originalPatterns: OutboundRoutePattern[],
  gateways: OutboundRouteGateway[],
  originalGateways: OutboundRouteGateway[]
): boolean {
  return (
    JSON.stringify(formData) !== JSON.stringify(originalData) ||
    JSON.stringify(patterns) !== JSON.stringify(originalPatterns) ||
    JSON.stringify(gateways) !== JSON.stringify(originalGateways)
  );
}

